import { Tabs, Button, Col, Row, message, Radio } from "antd";
import UploadedConsents from './UploadedConsents';
import moment from "moment";
import { React, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HasAccess } from "@permify/react-role";
import { handlers } from "../../../helpers/helpers";
import { getCookie } from '../../../services/cookies';
import Notes from "../../components/Notes";
import countrySettings from "../countrySettings.json";

const ServiceDetail = ({
  services,
  loading,
  setLoading,
  patientData = {},
  admissions,
  consents,
  fetchConsents,
  consentsLoading
}) => {
  const [selectedButtonId, setSelectedButtonId] = useState();
  const [downloadingConstancyId, setDownloadingConstancyId] = useState();

  const [currentConsentsPage, setCurrentConsentsPage] = useState(1);
  const perConsentsPage = 10;
  const lastConsentsPage = consents ? Math.ceil(consents.length / perConsentsPage) : 1;
  const loadMoreConsents = (event) => {
    const direction = event.target.value;
    const newPage = direction === 'prev' ? currentConsentsPage - 1 : currentConsentsPage + 1;
    setCurrentConsentsPage(newPage);
  };

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;
  const lastPage = services ? Math.ceil(services.length / perPage) : 1;
  const loadMore = (event) => {
    const direction = event.target.value;
    const newPage = direction === 'prev' ? currentPage - 1 : currentPage + 1;
    setCurrentPage(newPage);
  };

  const { t } = useTranslation();
  const { TabPane } = Tabs;
  const countryCode = getCookie("country_code");
  const roles = getCookie('roles');

  const isConstancyEnabled = countrySettings?.patientForm?.downloadAttentionConstancy?.[countryCode] || false;

  const reservationColSize = 4;
  const entryDateColSize = isConstancyEnabled ? 3 : 4;
  const serviceColSize = isConstancyEnabled ? 5 : 7;
  const resultColSize = isConstancyEnabled ? 3 : 4;
  const reportColSize = isConstancyEnabled ? 4 : 5;
  const constancyColSize = isConstancyEnabled ? 5 : 0;

  const formatDate = (date) => {
    return moment(date, "YYYY-MM-DDTHH:mm:ssZ").format("DD-MM-YYYY");
  };

  const downloadSignedReport = async (action, id, type) => {
    try {
      setLoading(loading => { return { ...loading, download: true } });
      setSelectedButtonId(id)
      const fileName = `${patientData.full_name}_${patientData.document_number}_${id}.pdf`
      await handlers.downloadSignedReport({
        id,
        type,
        countryCode,
        fileName,
        action,
      })
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al descargar informe", style: { marginTop: "35vh", fontSize: 16, }, });
    } finally {
      setLoading(loading => { return { ...loading, download: false } });
      setSelectedButtonId()
    }
  };

  const downloadConstancyReport = async (consultationId) => {
    try {
      setDownloadingConstancyId(consultationId);
      const fileName = `${patientData.full_name}_Constancia_Atencion_${consultationId}.pdf`;
      await handlers.downloadSignedReport({
        id: consultationId,
        type: 'attention_constancy',
        countryCode,
        fileName,
        action: 'download',
      });
    } catch (error) {
      console.error(error);
      message.error({
        content: "Error al descargar la constancia de atención",
        style: { marginTop: "35vh", fontSize: 16 },
      });
    } finally {
      setDownloadingConstancyId(undefined);
    }
  };

  const customTableHeader = () => {
    return (
      <Row className="table-header">
        <Col
          lg={reservationColSize}
          className="align-item-center-start p-10"
        >
          <h3>
            {t('patientPage.serviceDetail.reservation')}
          </h3>
        </Col>
        <Col
          lg={entryDateColSize}
          className="center-item p-10"
        >
          <h3 className="text-center">Fecha de ingreso</h3>
        </Col>
        <Col
          lg={serviceColSize}
          className="center-item p-10"
        >
          <h3 className="text-center">
            {t('patientPage.serviceDetail.service')}
          </h3>
        </Col>
        <Col
          lg={resultColSize}
          className="center-item p-10"
        >
          <h3 className="text-center">Resultado</h3>
        </Col>
        <Col
          lg={reportColSize}
          className="center-item p-10"
        >
          <h3 className="text-center">Descargar Informe</h3>
        </Col>
        {isConstancyEnabled && (
          <Col
            lg={constancyColSize}
            className="center-item p-10"
          >
            <h3 className="text-center">Constancia de atención</h3>
          </Col>
        )}
      </Row>
    );
  };

  const customTableBody = (item, index) => {
    return (
      <Row className="table-body mb-15" key={index}>
        <Col
          lg={reservationColSize}
          xs={12}
          className="align-item-center-start p-10"
        >
          <h3 className="hide-desktop text-center">
            {t('patientPage.serviceDetail.reservation')}
          </h3>
          <p className="mb-0">{item.reservation_number}</p>
        </Col>
        <Col
          lg={entryDateColSize}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Fecha de ingreso</h3>
          <p className="mb-0">{item.admission_date && formatDate(item.admission_date)}</p>
        </Col>
        <Col
          lg={serviceColSize}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {t('patientPage.serviceDetail.service')}
          </h3>
          {item.signed_at !== null
            ? <HasAccess
              roles={['equipo_psicologia', 'doctor', 'direccion_medica']}
              permissions='downloadReport'
              renderAuthFailed={<p className="mb-0">{item.prestation_name}</p>}
            >
              <Link
                to={`/consulta-medica/${item.id}`}
              >
                {item.prestation_name}
              </Link>
            </HasAccess>
            : <p className="mb-0">{item.prestation_name}</p>
          }
        </Col>
        <Col
          lg={resultColSize}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Resultado</h3>
          {
            item.consultation_text ?
              <p className={`text-center ${item.approved ? 'approved' : 'reprobate'}approved mb-0`}>
                {item.consultation_text}
              </p>
              :
              (
                item.approved !== null ?
                  <>
                    {
                      item.approved ?
                        <p className="text-center approved mb-0">Aprobado</p>
                        :
                        <p className="text-center reprobate mb-0">No aprobado</p>
                    }
                  </>
                  :
                  <p className="text-center mb-0">-</p>
              )}
        </Col>
        <Col lg={reportColSize} xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Descargar Informe</h3>
          {
            item.signed_at !== null ?
              <>
                {
                  <HasAccess
                    roles={['super_usuario', 'branch_office_manager', 'equipo_psicologia',
                      'doctor', 'direccion_medica', 'administracion', 'equipo_medico']}
                    permissions='downloadReport'
                  >
                    <Button
                      className="cmt-button secondary outline btn-download-report"
                      type="secondary"
                      icon={<span className="icon-system-download" />}
                      size={"small"}
                      loading={loading.download && selectedButtonId === item.id}
                      onClick={() =>
                        downloadSignedReport('download', item.id, 'signed')
                      }
                    />
                  </HasAccess>
                }
              </>
              :
              <p className="mb-0">No disponible</p>
          }
        </Col>
        {
          isConstancyEnabled && (
            <Col lg={constancyColSize} xs={24}
              className="center-item p-10"
            >
              <h3 className="hide-desktop text-center">Constancia de atención</h3>
              <Button
                className="cmt-button secondary outline btn-download-report"
                type="secondary"
                icon={<span className="icon-system-download" />}
                size={"small"}
                loading={downloadingConstancyId === item.id}
                onClick={() =>
                  downloadConstancyReport(item.id)
                }
              />
            </Col>
          )
        }
      </Row>
    );
  };

  return (
    <Row justify="center">
      <Col lg={22} xs={22}>
        <Tabs className="service-detail-tabs" defaultActiveKey="1" tabPosition="top" tabBarGutter={48}>
          <TabPane tab={t('patientPage.tabs.performedServices')} key="1">
            <Row justify="center">
              <Col lg={22} xs={22} className="mt-30 mb-20">
                <h2 className="mb-15">{t('patientPage.serviceDetail.services')} realizados</h2>
                {services && services.length > 0 ? (
                  <>
                    {customTableHeader()}
                    {services
                      .slice((currentPage - 1) * perPage, currentPage * perPage)
                      .map((item, index) => customTableBody(item, (currentPage - 1) * perPage + index))}
                    <Row justify="center">
                      <Col lg={24} xs={24} className="mt-15 mb-20">
                        <Radio.Group className="cmt-pagination">
                          <Radio.Button value="prev" disabled={currentPage <= 1} onClick={loadMore}>
                            Anterior
                          </Radio.Button>
                          <Radio.Button value="next" disabled={currentPage >= lastPage} onClick={loadMore}>
                            Siguiente
                          </Radio.Button>
                        </Radio.Group>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <p className="text-center">{t('patientPage.serviceDetail.disclaimer')}</p>
                )}
              </Col>
            </Row>
          </TabPane>
          <TabPane tab={t('patientPage.tabs.loadedConsents')} key="2">
            {!consentsLoading && (
              <UploadedConsents
                admissions={admissions}
                consents={
                  Array.isArray(consents) ? consents.slice((currentConsentsPage - 1) * perConsentsPage, currentConsentsPage * perConsentsPage) : []
                }
                patientId={patientData.id || ''}
                fetchConsents={fetchConsents}
              />
            )}
            <Row justify="center">
              <Col lg={22} xs={22} className="mt-15 mb-20">
                <Radio.Group className="cmt-pagination">
                  <Radio.Button value="prev" disabled={currentConsentsPage <= 1} onClick={loadMoreConsents}>
                    Anterior
                  </Radio.Button>
                  <Radio.Button value="next" disabled={currentConsentsPage >= lastConsentsPage} onClick={loadMoreConsents}>
                    Siguiente
                  </Radio.Button>
                </Radio.Group>
              </Col>
            </Row>
          </TabPane>
          {roles.includes('gerente_operaciones') &&
          <TabPane tab={t('patientPage.tabs.notes')} key="3">
            <Notes id={patientData.id} type="patient" edit={true} showTagging={true} instructions={t('patientPage.notes.instructions')} />
          </TabPane>
          }
        </Tabs>
      </Col>
    </Row>
  );
};

export default ServiceDetail;
